/**
 * FAQ Accordion — keyboard-navigable, ARIA-driven collapsible list.
 * Uses native <button aria-expanded>/[hidden] rather than <details> so the
 * open/close animation can be handled by JS + CSS transitions consistently.
 */
export function initFaqAccordion() {
	document.querySelectorAll( '[data-faq-accordion]' ).forEach( initAccordion );
}

function initAccordion( accordion ) {
	if ( accordion.dataset.faqInit ) return;
	accordion.dataset.faqInit = '1';

	const allowMultiple = accordion.dataset.allowMultiple === '1';
	const triggers = [ ...accordion.querySelectorAll( '.faq-accordion__trigger' ) ];

	const setOpen = ( trigger, panel, open ) => {
		trigger.setAttribute( 'aria-expanded', String( open ) );
		trigger.closest( '.faq-accordion__item' )?.classList.toggle( 'is-open', open );

		if ( open ) {
			panel.hidden = false;
			// Force reflow so the max-height transition actually runs.
			panel.style.maxHeight = '0px';
			void panel.offsetHeight;
			panel.style.maxHeight = `${ panel.scrollHeight }px`;
		} else {
			panel.style.maxHeight = `${ panel.scrollHeight }px`;
			void panel.offsetHeight;
			panel.style.maxHeight = '0px';
		}
	};

	triggers.forEach( ( trigger ) => {
		const panel = document.getElementById( trigger.getAttribute( 'aria-controls' ) || '' );
		if ( ! panel ) return;

		panel.addEventListener( 'transitionend', () => {
			if ( trigger.getAttribute( 'aria-expanded' ) === 'false' ) {
				panel.hidden = true;
			}
		} );

		trigger.addEventListener( 'click', () => {
			const isOpen = trigger.getAttribute( 'aria-expanded' ) === 'true';

			if ( ! allowMultiple && ! isOpen ) {
				triggers.forEach( ( other ) => {
					if ( other === trigger ) return;
					const otherPanel = document.getElementById( other.getAttribute( 'aria-controls' ) || '' );
					if ( otherPanel && other.getAttribute( 'aria-expanded' ) === 'true' ) {
						setOpen( other, otherPanel, false );
					}
				} );
			}

			setOpen( trigger, panel, ! isOpen );
		} );

		// Arrow-key navigation between questions, per the WAI-ARIA accordion pattern.
		trigger.addEventListener( 'keydown', ( e ) => {
			const index = triggers.indexOf( trigger );
			let target  = null;

			if ( e.key === 'ArrowDown' ) target = triggers[ ( index + 1 ) % triggers.length ];
			if ( e.key === 'ArrowUp' ) target = triggers[ ( index - 1 + triggers.length ) % triggers.length ];
			if ( e.key === 'Home' ) target = triggers[ 0 ];
			if ( e.key === 'End' ) target = triggers[ triggers.length - 1 ];

			if ( target ) {
				e.preventDefault();
				target.focus();
			}
		} );
	} );
}

